/**
 * SceneDetailsPanel Component - Refined Right Sidebar
 * Clean design with essential information and low-alpha icon wrappers
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { Badge, Button, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Palette,
  Layout as LayoutIcon,
  FileText,
  Tag as TagIcon,
  Clock,
  PlayCircle,
  Settings,
} from 'lucide-react';
import { useAtom } from 'jotai';
import { Scene, MultiSceneProject } from '../../../../types/scene';
import { SceneTagSelector } from '../scenes/SceneCard/SceneTagSelector';
import { SceneTagType } from '../../../../types/template';
import { getPresetByKey } from '../../presets/layoutPresetsPreview';
import { sceneRecordingStateAtom } from '../../atoms/sceneAtoms';
import './SceneDetailsPanel.scss';

interface SceneDetailsPanelProps {
  scene: Scene | null | undefined;
  project: MultiSceneProject | null;
  onSceneSelect?: (sceneId: string) => void;
  onClose?: () => void;
  onSceneTagsChange?: (sceneId: string, tags: SceneTagType[]) => void;
  isUpdatingScene?: boolean;
  canvasRatio?: string;
  liveBackgroundColor?: string | null;
  liveBackgroundImage?: HTMLImageElement | null;
  selectedBackground?: any;
  onPreviewScene?: (sceneId: string) => void;
  onRetakeScene?: (sceneId: string) => void;
  onDuplicateScene?: (sceneId: string) => void;
  onDeleteScene?: (sceneId: string) => void;
  onExportScene?: (sceneId: string) => void;
  onOpenTeleprompter?: () => void;
  onConfirmScene?: (sceneId: string) => void;
  onResetBackground?: () => void;
  onEditSettings?: (sceneId: string) => void;
}

export const SceneDetailsPanel: React.FC<SceneDetailsPanelProps> = ({
  scene,
  project,
  onSceneSelect,
  onClose,
  onSceneTagsChange,
  isUpdatingScene = false,
  canvasRatio = '16:9',
  liveBackgroundColor,
  liveBackgroundImage,
  selectedBackground,
  onPreviewScene,
  onRetakeScene,
  onDuplicateScene,
  onDeleteScene,
  onExportScene,
  onOpenTeleprompter,
  onConfirmScene,
  onResetBackground,
  onEditSettings,
}) => {
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [sceneRecordingState] = useAtom(sceneRecordingStateAtom);
  const isRecording = sceneRecordingState.isRecording;

  // Focus management for a11y
  useEffect(() => {
    if (scene) {
      previousFocusRef.current = document.activeElement as HTMLElement;
    }
  }, [scene]);

  // ESC key handler for a11y
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose]);

  const handleNavigatePrevious = useCallback(() => {
    if (!scene || !project) return;
    const currentIndex = project.scenes.findIndex(s => s.id === scene.id);
    if (currentIndex > 0 && onSceneSelect) {
      onSceneSelect(project.scenes[currentIndex - 1].id);
    }
  }, [scene, project, onSceneSelect]);

  const handleNavigateNext = useCallback(() => {
    if (!scene || !project) return;
    const currentIndex = project.scenes.findIndex(s => s.id === scene.id);
    if (currentIndex < project.scenes.length - 1 && onSceneSelect) {
      onSceneSelect(project.scenes[currentIndex + 1].id);
    }
  }, [scene, project, onSceneSelect]);
  
  // Check if scene has separate stream recordings with S3 URLs (must be before early return)
  const hasSeparateStreamRecording = React.useMemo(() => {
    if (!scene?.metadata) return false;
    try {
      const metadata = typeof scene.metadata === 'string' 
        ? JSON.parse(scene.metadata) 
        : scene.metadata;
      if (metadata.recordingType !== 'separate_streams') return false;
      const streams = metadata.streams;
      if (!streams) return false;
      const hasWebcamWithKeys = streams.webcam?.segments?.some((s: any) => s.key);
      const hasScreenWithKeys = streams.screen?.segments?.some((s: any) => s.key);
      const hasAudioWithKeys = streams.audio?.segments?.some((s: any) => s.key || s.hasMic);
      return hasWebcamWithKeys || hasScreenWithKeys || hasAudioWithKeys;
    } catch {
      return false;
    }
  }, [scene?.metadata]);

  if (!scene) {
    return (
      <div className="scene-details-panel" ref={panelRef}>
        <div className="empty-state">
          <FileText size={48} />
          <p>Select a scene to view details</p>
        </div>
      </div>
    );
  }

  const sceneIndex = project?.scenes.findIndex(s => s.id === scene.id) ?? 0;
  const sceneNumber = sceneIndex + 1;
  const totalScenes = project?.scenes.length ?? 1;
  const isPreviousDisabled = sceneIndex === 0;
  const isNextDisabled = sceneIndex === totalScenes - 1;

  // Format duration
  const formatDuration = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Get status badge
  const getStatusBadge = () => {
    switch (scene.status) {
      case 'recorded':
        return <Badge status="success" text="Recorded" />;
      case 'recording':
        return <Badge status="processing" text="Recording" />;
      case 'pending_confirmation':
      case 'pending_upload':
        return <Badge status="warning" text="Pending Confirmation" />;
      case 'failed':
        return <Badge status="error" text="Failed" />;
      case 'pending':
      default:
        return <Badge status="default" text="Pending" />;
    }
  };

  // Get background display (prioritize live toolbar values, fallback to saved scene.background)
  const getBackgroundDisplay = () => {
    // Prioritize live toolbar selection
    if (liveBackgroundImage) {
      return {
        label: 'Image',
        color: '#e5e7eb',
        type: 'image',
        imageUrl: liveBackgroundImage.src
      };
    }
    
    if (liveBackgroundColor) {
      // Check if it's a gradient (contains 'gradient' keyword)
      const isGradient = liveBackgroundColor.includes('gradient');
      return {
        label: isGradient ? 'Gradient' : `Solid: ${liveBackgroundColor}`,
        color: liveBackgroundColor,
        type: isGradient ? 'gradient' : 'solid'
      };
    }

    // Fallback to saved scene background (check both metadata and direct property)
    const background = scene.background || (scene as any).metadata?.background;
    
    if (!background) {
      return {
        label: 'None',
        color: '#e5e7eb',
        type: 'none'
      };
    }

    switch (background.type) {
      case 'solid':
        return {
          label: `Solid: ${background.colors?.[0] || '#000'}`,
          color: background.colors?.[0] || '#000',
          type: 'solid'
        };
      case 'color': {
        const colorValue = background.value || '#000000';
        return {
          label: `Solid: ${colorValue}`,
          color: colorValue,
          type: 'solid'
        };
      }
      case 'gradient':
        return {
          label: 'Gradient',
          color: `linear-gradient(135deg, ${background.colors?.join(', ')})`,
          type: 'gradient'
        };
      case 'image':
        return {
          label: 'Image',
          color: '#e5e7eb',
          type: 'image',
          imageUrl: background.imageUrl
        };
      default:
        return {
          label: 'Unknown',
          color: '#e5e7eb',
          type: 'unknown'
        };
    }
  };

  const backgroundDisplay = getBackgroundDisplay();
  const hasScript = !!scene.script && scene.script.trim().length > 0;
  const presetInfo = scene.layoutPreset ? getPresetByKey(scene.layoutPreset) : null;
  const layoutName = presetInfo?.name || scene.layoutPreset || 'Not configured';
  
  // Check if scene needs confirmation (both pending_upload and pending_confirmation show "Confirm Scene")
  const isPendingConfirmation = scene.status === 'pending_confirmation' || scene.status === 'pending_upload';

  // Check if background has been changed from the scene's default
  const isBackgroundChanged = (() => {
    const sceneBackground = scene.background || (scene as any).metadata?.background;
    
    // No live override means using scene default
    if (!liveBackgroundColor && !liveBackgroundImage) {
      return false;
    }
    
    // If there's a live background image from toolbar
    if (liveBackgroundImage) {
      // Compare with scene's default image URL
      if (sceneBackground?.type === 'image' && sceneBackground?.imageUrl) {
        // Normalize URLs for comparison (handle relative vs absolute paths)
        const liveUrl = liveBackgroundImage.src;
        const sceneUrl = sceneBackground.imageUrl;
        // Check if they end with the same path (handles full URL vs relative path)
        return !liveUrl.endsWith(sceneUrl) && !sceneUrl.endsWith(liveUrl.split('/').pop() || '');
      }
      return true; // Scene default is not an image
    }
    
    // If there's a live background color from toolbar
    if (liveBackgroundColor) {
      if (!sceneBackground) {
        // No scene background defined, check if it's the default color
        return liveBackgroundColor !== '#1F2937';
      }
      
      if (sceneBackground.type === 'solid' && sceneBackground.colors?.[0]) {
        return liveBackgroundColor.toLowerCase() !== sceneBackground.colors[0].toLowerCase();
      }
      if (sceneBackground.type === 'color' && sceneBackground.value) {
        return liveBackgroundColor.toLowerCase() !== sceneBackground.value.toLowerCase();
      }
      if (sceneBackground.type === 'gradient') {
        // Check if the gradient CSS matches
        const gradientCss = sceneBackground.colors?.length >= 2
          ? `linear-gradient(135deg, ${sceneBackground.colors.join(', ')})`
          : sceneBackground.colors?.[0];
        return liveBackgroundColor !== gradientCss && 
               liveBackgroundColor.toLowerCase() !== (sceneBackground.colors?.[0] || '').toLowerCase();
      }
      if (sceneBackground.type === 'image') {
        // Live is a color but scene default is an image - that's a change
        return true;
      }
      return false;
    }
    
    return false;
  })();

  return (
    <div className="scene-details-panel" ref={panelRef}>
      {/* Panel Header - Streamlined */}
      <div className="panel-header">
        <div className="header-top-row">
          <div className="scene-nav-row">
            <button
              className="nav-button nav-previous"
              onClick={handleNavigatePrevious}
              disabled={isPreviousDisabled}
              aria-label="Previous scene"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="scene-number-label">Scene {sceneNumber} of {totalScenes}</span>
            <button
              className="nav-button nav-next"
              onClick={handleNavigateNext}
              disabled={isNextDisabled}
              aria-label="Next scene"
            >
              <ChevronRight size={18} />
            </button>
          </div>
          <button
            className="close-button"
            onClick={onClose}
            aria-label="Close scene details"
          >
            <X size={18} />
          </button>
        </div>

        <div className="header-title-row">
          <h2 className="scene-title">{scene.title}</h2>
          <div className="scene-status-badge">{getStatusBadge()}</div>
        </div>

        {isUpdatingScene && (
          <div className="saving-indicator">
            <Spin indicator={<LoadingOutlined style={{ fontSize: 12, color: '#4A3CFA' }} spin />} />
            <span>Saving...</span>
          </div>
        )}

        <div className="header-action">
          <Button
            type="primary"
            className="preview-button"
            icon={<PlayCircle size={16} />}
            onClick={() => {
              if (isPendingConfirmation) {
                onConfirmScene?.(scene.id);
              } else {
                onPreviewScene?.(scene.id);
              }
            }}
            disabled={isRecording || scene.status === 'pending' || (!isPendingConfirmation && !scene.videoUrl && !hasSeparateStreamRecording && (!scene.videoChunks || scene.videoChunks.length === 0))}
            aria-label={isPendingConfirmation ? "Confirm scene" : "Preview scene video"}
          >
            {isPendingConfirmation ? 'Confirm Scene' : 'Preview Recording'}
          </Button>
          <Button
            type="default"
            ghost
            className="edit-settings-button"
            icon={<Settings size={16} />}
            onClick={() => onEditSettings?.(scene.id)}
            aria-label="Edit scene settings"
          >
            Edit Scene Settings
          </Button>
        </div>
      </div>

      {/* Panel Content */}
      <div className="panel-content">
        {/* Duration Card */}
        <section className="detail-card">
          <div className="card-header">
            <div className="icon-wrapper">
              <Clock size={16} />
            </div>
            <span className="card-title">Duration</span>
          </div>
          <div className="card-content">
            <div className="info-grid">
              {scene.status === 'recorded' ? (
                <>
                  <span className="info-label">Recorded</span>
                  <span className="info-value">{scene.duration > 0 ? formatDuration(scene.duration) : '—'}</span>
                </>
              ) : scene.duration > 0 ? (
                <>
                  <span className="info-label">Estimated</span>
                  <span className="info-value">~{formatDuration(scene.duration)}</span>
                </>
              ) : (
                <>
                  <span className="info-label">Duration</span>
                  <span className="info-value">—</span>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Layout Card */}
        <section className="detail-card">
          <div className="card-header">
            <div className="icon-wrapper">
              <LayoutIcon size={16} />
            </div>
            <span className="card-title">Layout</span>
          </div>
          <div className="card-content">
            <div className="info-grid">
              <span className="info-label">Preset</span>
              <span className="info-value">{layoutName}</span>
              <span className="info-label">Ratio</span>
              <span className="info-value">{canvasRatio}</span>
            </div>
          </div>
        </section>

        {/* Background Card */}
        <section className="detail-card">
          <div className="card-header">
            <div className="icon-wrapper">
              <Palette size={16} />
            </div>
            <span className="card-title">Background</span>
          </div>
          <div className="card-content">
            <div className="background-info">
              <div
                className="background-preview"
                style={{
                  backgroundColor: backgroundDisplay.type === 'solid' ? backgroundDisplay.color : undefined,
                  backgroundImage: backgroundDisplay.type === 'gradient' 
                    ? backgroundDisplay.color 
                    : backgroundDisplay.type === 'image' && backgroundDisplay.imageUrl
                    ? `url(${backgroundDisplay.imageUrl})`
                    : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <div className="background-details">
                <span className="background-type">{backgroundDisplay.type === 'image' ? 'Image' : backgroundDisplay.type === 'gradient' ? 'Gradient' : 'Solid Color'}</span>
                {backgroundDisplay.type === 'solid' && (
                  <span className="background-value">{backgroundDisplay.color}</span>
                )}
              </div>
            </div>
            {isBackgroundChanged && onResetBackground && (
              <button 
                className="reset-background-button" 
                onClick={onResetBackground}
                aria-label="Reset background to default"
              >
                Reset To Default
              </button>
            )}
          </div>
        </section>

        {/* Script Card */}
        <section className="detail-card">
          <div className="card-header">
            <div className="icon-wrapper">
              <FileText size={16} />
            </div>
            <span className="card-title">Script</span>
          </div>
          <div className="card-content">
            {hasScript ? (
              <>
                <div className="script-preview">
                  {scene.script}
                </div>
                <button 
                  className="teleprompter-button" 
                  onClick={onOpenTeleprompter}
                  aria-label="Open teleprompter"
                >
                  <FileText size={14} />
                  Open Teleprompter
                </button>
              </>
            ) : (
              <div className="empty-state-inline">
                <span>No script added</span>
                <button 
                  className="teleprompter-button" 
                  onClick={onOpenTeleprompter}
                  aria-label="Add script in teleprompter"
                >
                  <FileText size={14} />
                  Add Script
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Tags Card */}
        <section className="detail-card">
          <div className="card-header">
            <div className="icon-wrapper">
              <TagIcon size={16} />
            </div>
            <span className="card-title">Tags</span>
          </div>
          <div className="card-content">
            <p className="helper-text">Select up to 3 tags to categorize this scene</p>
            <SceneTagSelector
              selectedTags={scene.tags || []}
              onChange={(tags: SceneTagType[]) => onSceneTagsChange?.(scene.id, tags)}
              maxTags={3}
              isUpdating={isUpdatingScene}
            />
          </div>
        </section>
      </div>
    </div>
  );
};
